import { Monitor, Moon, Sun } from 'lucide-react';

import { useTheme, type Theme } from '@components/providers/ThemeProvider';
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from '@ui/dropdown-menu';
import { cn } from '@lib/utils';

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * ThemeToggle — the light/dark/system switcher inside the user dropdown
 * (`NavUser`). It renders as a single menu row: a mono micro-label plus a
 * three-way segmented control, so the current choice reads at a glance instead
 * of hiding one level down.
 *
 * Must be rendered inside a `DropdownMenuContent` — the segments are menu radio
 * items, which gives arrow-key navigation and `menuitemradio` semantics for
 * free. Selecting a segment deliberately keeps the menu open: the app flips
 * theme underneath it, and that live change is the feedback.
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="flex items-center justify-between gap-3 px-2 py-1.5" data-testid="theme-toggle">
      <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-muted-foreground">
        Theme
      </span>
      <DropdownMenuRadioGroup
        value={theme}
        onValueChange={value => setTheme(value as Theme)}
        aria-label="Theme"
        className="flex items-center gap-0.5"
        data-testid="theme-toggle-options"
      >
        {OPTIONS.map(({ value, label, icon: Icon }) => (
          <DropdownMenuRadioItem
            key={value}
            value={value}
            // Keep the menu open so the theme change is visible in place.
            onSelect={event => event.preventDefault()}
            aria-label={label}
            title={label}
            className={cn(
              // The shared radio item reserves an absolute indicator slot on the
              // left; these segments are icon-only, so hide the dot.
              '[&>span:first-child]:hidden',
              'h-7 w-7 justify-center rounded-[4px] p-0 focus:text-foreground',
              theme === value
                ? 'bg-accent text-foreground ring-1 ring-border'
                : 'text-muted-foreground'
            )}
            data-testid={`theme-option-${value}`}
          >
            <Icon className="size-4" />
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </div>
  );
}
